import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import styled from 'styled-components';
import axios from 'axios';
import LoanForwardForecastDrawer from './LoanForwardForecastDrawer';
import LoanActivityDrawer from './LoanActivityDrawer';
import { parseLocalDate } from './LoanFacilityCardDrawer';
import { CreditActionModal, CreditActionKind, CreditPositionLite } from './CreditLoanActionModals';
import {
  Banknote, Briefcase, CheckCircle2, Database, History,
  Landmark, Layers, LineChart, Percent, Plus, Search, Ticket,
} from 'lucide-react';
import type { LucideIcon } from 'lucide-react';
import {
  iborPositionPath,
  leveragedLoanTicketPath,
  leveragedLoanTicketState,
  loan360Path,
  securityMasterPath,
} from '../../utils/loanNavigation';
import { CORE_CREDIT_LIFECYCLE_ACTIONS } from './CreditLifecycleActions';
import { AxiomMetricCard, AxiomMetricGrid, AxiomStatusBadge } from '../shared';
import {
  CellPrimary, CellSub, InventoryActions, InventoryAwaiting, InventoryButton,
  InventoryFilters, InventoryFootnote, InventorySearchBox, InventorySelect,
  InventoryState, InventoryTable, InventoryTableWrap, InventoryToolbar,
  LifecycleAction, NavAction, RowActionDivider, RowActions,
} from './shared/CreditInventoryKit';

const LL_SPECIALIZED_ACTIONS: Array<{ kind: CreditActionKind; title: string; Icon: LucideIcon; color: string }> = [
  { kind: 'pik-capitalize', title: 'Capitalize PIK', Icon: Layers, color: '#c4b5fd' },
  { kind: 'pik-election', title: 'PIK election', Icon: Percent, color: '#c4b5fd' },
];

const llToLite = (loan: any): CreditPositionLite => ({
  loanRecordId: loan.id,
  securityId: loan.securityId || loan.security?.id || 0,
  loanName: loan.loanName || loan.borrowerName || `Loan ${loan.id}`,
  borrowerName: loan.borrowerName,
  portfolioId: loan.portfolioId || loan.portfolio?.id,
  cusip: loan.cusip || loan.security?.cusip,
  // This screen is facility reference data, not a portfolio position. Never
  // pass deprecated tranche-header funded/unfunded values into shared modals.
  fundedAmount: undefined,
  currentCommitment: loan.currentCapacity,
  unfundedAmount: undefined,
  spreadBps: loan.spreadBps,
  allInRate: loan.allInRate,
  baseRate: loan.baseRate,
  maturityDate: loan.maturityDate,
  status: loan.status,
  facilityType: loan.facilityType,
  ddtlCommitment: loan.currentCapacity ?? undefined,
  ddtlFunded: loan.globalOutstanding ?? undefined,
  ddtlUndrawn: loan.globalAvailability ?? undefined,
  ddtlExpiry: loan.ddtlExpiry ?? undefined,
  globalFacilityOutstanding: loan.globalOutstanding ?? undefined,
  globalFacilityAvailability: loan.globalAvailability ?? undefined,
  drawWindowEnd: loan.ddtlExpiry ?? undefined,
});

const Container = styled.div<{ $embedded?: boolean }>`
  max-width: 1800px;
  margin: 0 auto;
  padding: ${props => props.$embedded ? '1rem' : '2rem'};
`;

// Toolbar, stat cards, table, status and row-action styling now come from the
// shared CreditInventoryKit + AxiomMetricCard so this screen matches the
// Referential Master loan table and the PC Loans screen exactly.

interface LeveragedLoan {
  id: number;
  loanName: string;
  borrowerName: string;
  facilityType: string;
  loanType: string;
  status: string;
  originalAmount: number;
  currentOutstanding: number;
  unfundedCommitment: number;
  spread: number;
  floor: number;
  maturityDate: string;
  agentBank: string;
  seniority: string;
  // Deal/tranche facility reference columns (FRD v1.3 D10 / V402). These are
  // GLOBAL facility amounts across all lenders — never fund exposure (FR-2.2).
  originalSize?: number | null;
  currentCapacity?: number | null;
  globalOutstanding?: number | null;
  globalAvailability?: number | null;
  outstandingAsOf?: string | null;
  outstandingSource?: string | null;
  facilityState?: string | null;
  trancheCode?: string | null;
  loanId?: string | null;
  cusip?: string | null;
  securityId?: number | null;
  security?: { id?: number; cusip?: string } | null;
  ddtlExpiry?: string | null;
  // PIK / non-accrual servicing flags (already returned by /api/credit/loans).
  // These are facility-level status flags, not balances — the pik_accrued
  // balance is a portfolio-scoped position figure that lives on the position
  // blotter, never on this facility reference list.
  pikElectionCurrent?: string | null;
  nonAccrualStatus?: boolean | null;
  nonAccrualDate?: string | null;
}

interface LeveragedLoansScreenProps {
  embedded?: boolean;
}

const LeveragedLoansScreen: React.FC<LeveragedLoansScreenProps> = ({ embedded = false }) => {
  const [loans, setLoans] = useState<LeveragedLoan[]>([]);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();
  const [error, setError] = useState<string | null>(null);
  const [statusFilter, setStatusFilter] = useState('ALL');
  const [searchTerm, setSearchTerm] = useState('');
  const [forecastLoan, setForecastLoan] = useState<LeveragedLoan | null>(null);
  const [activityLoan, setActivityLoan] = useState<LeveragedLoan | null>(null);
  const [actionCtx, setActionCtx] = useState<{ kind: CreditActionKind; position: CreditPositionLite } | null>(null);

  const fetchLoans = async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await axios.get('/api/credit/loans');
      const data = response.data;
      setLoans(Array.isArray(data) ? data : (data?.loans || data?.content || []));
    } catch (err) {
      console.error('Failed to fetch loans:', err);
      setError('Failed to load leveraged loans. Please try again.');
      setLoans([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchLoans(); }, []);

  const filteredLoans = loans.filter(loan => {
    const matchesStatus = statusFilter === 'ALL' || loan.status === statusFilter;
    const q = searchTerm.toLowerCase();
    const matchesSearch = [
      loan.loanName,
      loan.borrowerName,
      loan.loanId,
      loan.cusip,
      loan.security?.cusip,
      loan.agentBank,
    ].some(value => String(value || '').toLowerCase().includes(q));
    return matchesStatus && matchesSearch;
  });

  const formatCurrency = (value: number | undefined | null) => {
    if (value === undefined || value === null) return '$0';
    if (value >= 1000000000) return `$${(value / 1000000000).toFixed(2)}B`;
    if (value >= 1000000) return `$${(value / 1000000).toFixed(1)}M`;
    return `$${value.toLocaleString()}`;
  };

  // F37-adjacent (LL-FR-2): the loan book lists FACILITY REFERENCE amounts —
  // never fund Commitment/Unfunded. Fund exposure lives on the facility card
  // (portfolio panel) and comes from fact_positions.
  const facilitySize = (l: LeveragedLoan) =>
    l.currentCapacity ?? l.originalSize ?? null;
  const totalFacilitySize = loans.reduce((sum, l) => sum + (facilitySize(l) || 0), 0);
  const totalGlobalOutstanding = loans.reduce((sum, l) => sum + (l.globalOutstanding || 0), 0);
  const capacityEvidenceComplete = loans.length > 0 && loans.every(l => facilitySize(l) != null);
  const outstandingEvidenceComplete = loans.length > 0
    && loans.every(l => l.globalOutstanding !== null && l.globalOutstanding !== undefined);
  const performingCount = loans.filter(l => l.status === 'PERFORMING').length;

  // F24: date-only strings parsed as LOCAL dates (new Date("YYYY-MM-DD") is
  // UTC midnight → off-by-one in western timezones).
  const fmtLocalDate = (value?: string | null) => {
    const parsed = parseLocalDate(value);
    return parsed ? parsed.toLocaleDateString() : '-';
  };

  return (
    <Container $embedded={embedded}>
      {error && <InventoryState $error style={{ marginBottom: '1rem' }}>{error}</InventoryState>}

      <AxiomMetricGrid style={{ marginBottom: '1rem' }}>
        <AxiomMetricCard
          tone="cyan"
          icon={<Landmark />}
          label="Facility Capacity (Reference)"
          value={capacityEvidenceComplete ? formatCurrency(totalFacilitySize) : 'Awaiting evidence'}
        />
        <AxiomMetricCard
          tone="emerald"
          icon={<Banknote />}
          label="Global Outstanding (Reference)"
          value={outstandingEvidenceComplete ? formatCurrency(totalGlobalOutstanding) : 'Awaiting evidence'}
        />
        <AxiomMetricCard tone="violet" icon={<Layers />} label="Total Loans" value={loans.length} />
        <AxiomMetricCard tone="amber" icon={<CheckCircle2 />} label="Performing" value={performingCount} />
      </AxiomMetricGrid>

      <InventoryToolbar>
        <InventoryFilters>
          <InventorySearchBox>
            <Search size={15} />
            <input
              placeholder="Search borrower, loan ID, CUSIP, agent..."
              value={searchTerm}
              onChange={e => setSearchTerm(e.target.value)}
            />
          </InventorySearchBox>
          <InventorySelect value={statusFilter} onChange={e => setStatusFilter(e.target.value)}>
            <option value="ALL">All Status</option>
            <option value="PERFORMING">Performing</option>
            <option value="WATCHLIST">Watchlist</option>
            <option value="NON_PERFORMING">Non-Performing</option>
          </InventorySelect>
        </InventoryFilters>
        <InventoryActions>
          {/* F26: define LL loans through the canonical Reference form (same as PC),
              not a thin dashboard duplicate. */}
          <InventoryButton $primary type="button" onClick={() => navigate('/reference-data?tab=credit-loans&assetClass=LL')}>
            <Plus size={15} /> Add Loan
          </InventoryButton>
        </InventoryActions>
      </InventoryToolbar>

      {loading ? (
        <InventoryState>Loading leveraged loans…</InventoryState>
      ) : filteredLoans.length === 0 ? (
        <InventoryState>No leveraged loans match the current filters.</InventoryState>
      ) : (
        <InventoryTableWrap>
          <InventoryTable style={{ minWidth: 1180 }}>
            <thead>
              <tr>
                <th>Loan / Borrower</th>
                <th>Type</th>
                <th>Status</th>
                {/* Facility REFERENCE amounts (global, all lenders) — fund exposure
                    lives on the facility card's portfolio panel (LL-FR-2). */}
                <th title="Reference facility size across all lenders — not fund exposure">Ref Facility Size</th>
                <th title="Reference global borrower outstanding across all lenders — not fund exposure">Ref Global O/S</th>
                <th>Spread</th>
                <th>Maturity</th>
                <th>Agent</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {filteredLoans.map(loan => {
                const cusip = loan.cusip || loan.security?.cusip;
                const spreadBps = (loan as any).spreadBps ?? loan.spread;
                return (
                  <tr key={loan.id}>
                    <td>
                      <CellPrimary
                        as="a"
                        $clickable
                        href={loan360Path('LEVERAGED_LOAN', loan.id)}
                        onClick={(event: React.MouseEvent<HTMLAnchorElement>) => {
                          if (event.button !== 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;
                          event.preventDefault();
                          navigate(loan360Path('LEVERAGED_LOAN', loan.id));
                        }}
                        title="Open full-page Loan 360"
                      >
                        {loan.loanName || 'Unknown'}
                      </CellPrimary>
                      <CellSub>{loan.borrowerName || '—'}</CellSub>
                    </td>
                    <td>{(loan.facilityType || '').replace(/_/g, ' ') || '—'}</td>
                    <td>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 6, flexWrap: 'wrap' }}>
                        <span>{(loan.status || '—').replace(/_/g, ' ')}</span>
                        {loan.nonAccrualStatus && (
                          <AxiomStatusBadge
                            $tone="amber"
                            title={loan.nonAccrualDate ? `Non-accrual since ${fmtLocalDate(loan.nonAccrualDate)}` : 'Non-accrual'}
                          >
                            Non-Accrual
                          </AxiomStatusBadge>
                        )}
                        {loan.pikElectionCurrent && loan.pikElectionCurrent.toUpperCase().includes('PIK') && (
                          <AxiomStatusBadge $tone="violet" title={`PIK election: ${loan.pikElectionCurrent}`}>PIK</AxiomStatusBadge>
                        )}
                      </div>
                    </td>
                    <td>{facilitySize(loan) != null ? formatCurrency(facilitySize(loan)) : '—'}</td>
                    <td>
                      {loan.globalOutstanding !== null && loan.globalOutstanding !== undefined ? (
                        <span title={loan.outstandingAsOf ? `as of ${fmtLocalDate(loan.outstandingAsOf)} · ${loan.outstandingSource || 'unknown source'}` : undefined}>
                          {formatCurrency(loan.globalOutstanding)}
                        </span>
                      ) : (
                        <InventoryAwaiting>Awaiting agent evidence</InventoryAwaiting>
                      )}
                    </td>
                    <td>{spreadBps != null ? `S+${spreadBps}bp` : <InventoryAwaiting>Awaiting terms</InventoryAwaiting>}</td>
                    <td>{fmtLocalDate(loan.maturityDate)}</td>
                    <td>{loan.agentBank || '—'}</td>
                    <td>
                      <RowActions>
                        {/* Common actions (shared with PC) — same set + order */}
                        {cusip && (
                          <>
                            <NavAction tip="Security Master" onClick={() => navigate(securityMasterPath(cusip))}>
                              <Database size={15} />
                            </NavAction>
                            <NavAction
                              tip="Populate trade ticket"
                              onClick={() => navigate(
                                leveragedLoanTicketPath(cusip),
                                leveragedLoanTicketState({
                                  securityId: loan.securityId || loan.security?.id,
                                  cusip,
                                  securityName: loan.loanName,
                                  loanId: loan.id,
                                  loanName: loan.loanName,
                                  borrowerName: loan.borrowerName,
                                  facilityType: loan.facilityType,
                                }),
                              )}
                            >
                              <Ticket size={15} />
                            </NavAction>
                            <NavAction tip="IBOR exposure" onClick={() => navigate(iborPositionPath(cusip))}>
                              <Briefcase size={15} />
                            </NavAction>
                          </>
                        )}
                        <NavAction tip="Activity timeline" onClick={() => setActivityLoan(loan)}>
                          <History size={15} />
                        </NavAction>
                        {/* LL-specific */}
                        <NavAction tip="Forecast (Phase 2)" onClick={() => setForecastLoan(loan)}>
                          <LineChart size={15} />
                        </NavAction>
                        <RowActionDivider />
                        {CORE_CREDIT_LIFECYCLE_ACTIONS.map(action => (
                          <LifecycleAction
                            key={action.kind}
                            tip={action.title}
                            color={action.color}
                            onClick={() => setActionCtx({ kind: action.kind, position: llToLite(loan) })}
                          >
                            <action.Icon size={15} />
                          </LifecycleAction>
                        ))}
                        {LL_SPECIALIZED_ACTIONS.map(action => (
                          <LifecycleAction
                            key={action.kind}
                            tip={action.title}
                            color={action.color}
                            onClick={() => setActionCtx({ kind: action.kind, position: llToLite(loan) })}
                          >
                            <action.Icon size={15} />
                          </LifecycleAction>
                        ))}
                      </RowActions>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </InventoryTable>
        </InventoryTableWrap>
      )}

      {forecastLoan && (
        <LoanForwardForecastDrawer
          loanId={forecastLoan.id}
          loanName={forecastLoan.loanName}
          onClose={() => setForecastLoan(null)}
        />
      )}

      {activityLoan && (
        <LoanActivityDrawer
          loanId={activityLoan.id}
          loanName={activityLoan.loanName}
          onClose={() => setActivityLoan(null)}
        />
      )}

      {actionCtx && (
        <CreditActionModal
          kind={actionCtx.kind}
          assetKind="LL"
          position={actionCtx.position}
          onClose={() => setActionCtx(null)}
          onSuccess={() => { setActionCtx(null); fetchLoans(); }}
        />
      )}

      <InventoryFootnote>Showing {filteredLoans.length} of {loans.length} loans</InventoryFootnote>
    </Container>
  );
};

export default LeveragedLoansScreen;
